"use client";

import Image from "next/image";
import { useRef, useState } from "react";
import type { EngagementPageContent } from "@/types/engagement";
import { Reveal } from "@/components/ui/Reveal";
import {
  buildCalculatorCallHref,
  calculatePackageRecommendation,
  DEFAULT_PACKAGE_CALCULATOR_STATE,
  getRangeProgress,
  type PackageCalculatorState
} from "./engagement-package-calculator";

// Keep this interactive section's client bundle self-contained. Importing these
// constants from `shared.tsx` also pulled its server-rendered heading and icon
// dependencies into the hydration graph, which can leave Fast Refresh resolving
// an incompatible module after this section is enabled.
const engagementShell =
  "mx-auto box-border w-full min-w-0 max-w-[1440px] px-5 sm:px-8 md:px-12 lg:px-16 xl:px-[112px]";
const engagementSectionY = "py-8 md:py-12 lg:py-16 xl:py-20";

type PackageItem = EngagementPageContent["packages"]["items"][number];

function parseFirstNumber(value: string, fallback: number) {
  const parsed = Number(value.match(/\d+/)?.[0]);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function inferCapabilities(item: PackageItem) {
  const source = `${item.title} ${item.roles.join(" ")}`.toLowerCase();
  const capabilities = [
    ["Software Engineering", ["engineer", "developer", "software", "frontend", "backend"]],
    ["AI", ["ai ", "machine learning"]],
    ["Cloud", ["cloud"]],
    ["Data", ["data"]],
    ["Cybersecurity", ["security", "cyber"]],
    ["DevOps", ["devops"]],
    ["Product Design", ["designer", "design"]],
    ["QA", ["qa", "quality"]]
  ] as const;

  const matches = capabilities
    .filter(([, keywords]) => keywords.some((keyword) => source.includes(keyword)))
    .map(([capability]) => capability);

  return matches.length > 0 ? matches : ["Software Engineering"];
}

function stateFromPackage(item: PackageItem): PackageCalculatorState {
  const managedBy = item.managedBy.toLowerCase();

  return {
    people: Math.min(15, Math.max(1, parseFirstNumber(item.people, 6))),
    duration: Math.min(24, Math.max(1, parseFirstNumber(item.typicalLength, 13))),
    involvement: managedBy.includes("randstack") ? 2 : managedBy.includes("shared") ? 4 : 8,
    capabilities: inferCapabilities(item),
    scope: item.model.toLowerCase().includes("managed services")
      ? 2
      : item.model.toLowerCase().includes("project")
        ? 0
        : 1,
    management: managedBy.includes("randstack") ? 2 : managedBy.includes("shared") ? 1 : 0
  };
}

function RangeControl({
  id,
  label,
  value,
  min,
  max,
  minLabel,
  maxLabel,
  valueLabel,
  onChange
}: {
  id: string;
  label: string;
  value: number;
  min: number;
  max: number;
  minLabel: string;
  maxLabel: string;
  valueLabel: string;
  onChange: (value: number) => void;
}) {
  const rawProgress = getRangeProgress(value, min, max);
  const progress = Number(rawProgress.toFixed(2));

  return (
    <div className="flex h-[65px] w-full flex-col">
      <div className="flex h-[22px] items-start justify-between gap-4 text-sm leading-[1.55] tracking-[0.015em]">
        <label htmlFor={id} className="uppercase tracking-[0.1em] text-[#dee0e4]">
          {label}
        </label>
        <output htmlFor={id} className="shrink-0 font-semibold text-white">
          {valueLabel}
        </output>
      </div>

      <div className="relative mt-4 h-[27px] w-full">
        <span className="absolute left-0 top-0 block h-0.5 w-full">
          <Image src="/engagement-assets/packages-slider-track.svg" alt="" fill sizes="528px" className="object-fill" />
        </span>
        <input
          id={id}
          type="range"
          min={min}
          max={max}
          step={1}
          value={value}
          aria-valuetext={valueLabel}
          onChange={(event) => onChange(Number(event.target.value))}
          className="peer absolute -top-2 left-0 z-10 h-5 w-full cursor-pointer opacity-0"
        />
        <span
          aria-hidden="true"
          className="pointer-events-none absolute -top-[7px] size-4 -translate-x-1/2 rounded-full border-2 border-[#48cae4]/50 bg-[#48cae4] peer-focus-visible:ring-2 peer-focus-visible:ring-white peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-[#24247a]"
          style={{ left: `${progress}%` }}
        />
        <span className="absolute left-0 top-2 text-xs leading-[1.55] tracking-[0.015em] text-[#9ca3af]">
          {minLabel}
        </span>
        <span className="absolute right-0 top-2 text-xs leading-[1.55] tracking-[0.015em] text-[#9ca3af]">
          {maxLabel}
        </span>
      </div>
    </div>
  );
}

function ChoiceGroup({
  label,
  options,
  selected,
  onSelect,
  columns
}: {
  label: string;
  options: string[];
  selected: number;
  onSelect: (index: number) => void;
  columns: 3 | 4;
}) {
  return (
    <fieldset className="flex w-full flex-col gap-4">
      <legend className="text-sm font-normal uppercase leading-[1.55] tracking-[0.1em] text-[#dee0e4]">{label}</legend>
      <div
        className={`grid overflow-hidden rounded-[5px] border border-white/20 ${
          columns === 4
            ? "grid-cols-1 divide-y divide-white/20 md:grid-cols-4 md:divide-x md:divide-y-0"
            : "grid-cols-1 divide-y divide-white/20 md:grid-cols-3 md:divide-x md:divide-y-0"
        }`}
      >
        {options.map((option, index) => (
          <button
            key={option}
            type="button"
            aria-pressed={selected === index}
            onClick={() => onSelect(index)}
            className={`min-h-[60px] px-4 py-2 text-center text-sm leading-[1.55] tracking-[0.015em] transition-colors hover:bg-white/10 ${
              columns === 4 ? "md:min-h-[82px]" : ""
            } ${selected === index ? "bg-white/10 font-semibold text-white" : "font-normal text-[#dee0e4]"}`}
          >
            {option}
          </button>
        ))}
      </div>
    </fieldset>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex min-h-[25px] items-center justify-between gap-4">
      <dt className="text-sm font-normal leading-[1.55] tracking-[0.015em] text-[#dee0e4]">{label}</dt>
      <dd className="text-right text-base font-semibold leading-[1.55] tracking-[0.015em] text-white">{value}</dd>
    </div>
  );
}

export function EngagementPackagesSection({
  content,
  headingClassName
}: {
  content: EngagementPageContent["packages"];
  headingClassName: string;
}) {
  const calculatorRef = useRef<HTMLDivElement>(null);
  const [calculatorState, setCalculatorState] = useState<PackageCalculatorState>(DEFAULT_PACKAGE_CALCULATOR_STATE);
  const [calculatorOpen, setCalculatorOpen] = useState(false);
  const [activePackage, setActivePackage] = useState<number | null>(null);
  const items = Array.isArray(content.items) ? content.items : [];
  const calculator = content.calculator;
  const recommendation = calculatePackageRecommendation(calculatorState);

  function updateState<Key extends keyof PackageCalculatorState>(key: Key, value: PackageCalculatorState[Key]) {
    setCalculatorState((current) => ({ ...current, [key]: value }));
  }

  function focusCalculator() {
    requestAnimationFrame(() => calculatorRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }));
  }

  function startWithPackage(index: number) {
    setActivePackage(index);
    setCalculatorState(stateFromPackage(items[index]));
    setCalculatorOpen(true);
    focusCalculator();
  }

  function startCustomBuild() {
    setActivePackage(null);
    setCalculatorState(DEFAULT_PACKAGE_CALCULATOR_STATE);
    setCalculatorOpen(true);
    focusCalculator();
  }

  const activeItem = activePackage === null ? null : items[activePackage];

  const callHref = buildCalculatorCallHref(calculator.callCta.href, calculatorState, recommendation);

  return (
    <section data-node-id={activeItem ? "1308:13790" : "1308:14217"} className="bg-[#03045e] text-white">
      <div className={`${engagementShell} ${engagementSectionY} flex flex-col gap-6 md:gap-8 lg:gap-12`}>
        <header className="flex w-full max-w-[862px] flex-col items-start gap-4">
          <div className="flex w-full flex-col items-start gap-2">
            <div className="flex items-center gap-4">
              <p className="whitespace-nowrap text-base font-normal uppercase leading-[1.55] tracking-[0.1em] text-[#48cae4] xl:text-lg">
                {content.eyebrow}
              </p>
              <span aria-hidden="true" className="relative block h-[1.5px] w-[120px] shrink-0 sm:w-[209px]">
                <Image
                  src="/engagement-assets/packages-kicker-rule.svg"
                  alt=""
                  fill
                  sizes="(min-width: 640px) 209px, 120px"
                  className="object-fill"
                />
              </span>
            </div>
            <h2
              className={`${headingClassName} w-full whitespace-pre-line text-[32px] font-semibold leading-[1.2] tracking-[-0.03em] text-white md:text-[56px] xl:text-[72px]`}
            >
              {content.title}
            </h2>
          </div>
          <p className="w-full text-sm font-normal leading-[1.55] tracking-[0.015em] text-[#dee0e4] xl:text-base">
            {content.description}
          </p>
        </header>

        <div className="flex flex-col gap-6">
          <div className="grid items-stretch gap-6 xl:grid-cols-3">
            {items.map((item, index) => {
              const selected = index === activePackage;
              return (
                <Reveal key={`${item.title}-${index}`} delayMs={index * 100} className="h-full">
                <article
                  className={`flex h-full min-h-[601px] flex-col gap-6 rounded-[5px] border p-8 transition-all duration-300 ease-fold ${
                    selected
                      ? "border-[#48cae4] bg-[#48cae4]/20"
                      : "border-transparent bg-white/10 hover:-translate-y-1 hover:bg-white/[0.15]"
                  }`}
                >
                  <div className="flex min-h-[158px] flex-col gap-4 border-b border-white/20 pb-6">
                    <h3
                      className={`${headingClassName} text-2xl font-semibold leading-[1.3] tracking-[-0.03em] text-white xl:text-[32px]`}
                    >
                      {item.title}
                    </h3>
                    <p className="text-sm font-normal leading-[1.55] tracking-[0.015em] text-[#dee0e4] xl:text-base">
                      {item.description}
                    </p>
                  </div>

                  <div className="flex min-h-[150px] flex-col gap-4 border-b border-white/20 pb-6">
                    <p className="text-base font-semibold leading-[1.55] tracking-[0.015em] text-white">
                      {item.people}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {item.roles.map((role) => (
                        <span
                          key={role}
                          className="rounded-[2px] border border-white/20 bg-white/5 px-4 py-2 text-sm font-semibold leading-[1.55] tracking-[0.015em] text-white"
                        >
                          {role}
                        </span>
                      ))}
                    </div>
                  </div>

                  <dl className="flex min-h-[132px] flex-col gap-4 border-b border-white/20 pb-6">
                    <SummaryRow label="Model" value={item.model} />
                    <SummaryRow label="Typical length" value={item.typicalLength} />
                    <SummaryRow label="Managed by" value={item.managedBy} />
                  </dl>

                  <button
                    type="button"
                    onClick={() => startWithPackage(index)}
                    className="focus-ring group inline-flex items-center gap-2 self-start rounded-[8px] text-base font-semibold leading-[1.55] tracking-[0.015em] text-[#48cae4]"
                  >
                    {item.ctaLabel}
                    <Image
                      src="/engagement-assets/packages-arrow-cyan.svg"
                      alt=""
                      width={24}
                      height={24}
                      className="size-6 transition-transform group-hover:translate-x-1"
                    />
                  </button>
                </article>
                </Reveal>
              );
            })}
          </div>

          <div className="flex min-h-[149px] flex-col items-start justify-between gap-6 rounded-[5px] border border-[#48cae4] p-8 md:flex-row md:items-end">
            <div className="flex flex-col gap-4">
              <h3
                className={`${headingClassName} text-lg font-semibold leading-[1.3] tracking-[-0.03em] text-white md:text-[32px]`}
              >
                {calculatorOpen
                  ? activeItem
                    ? `${calculator.adjustingPrefix} ${activeItem.title}`
                    : calculator.buildTitle
                  : content.customTitle}
              </h3>
              <p className="text-sm font-normal leading-[1.55] tracking-[0.015em] text-[#dee0e4] xl:text-base">
                {calculatorOpen
                  ? activeItem
                    ? calculator.adjustingDescription
                    : calculator.buildDescription
                  : content.customDescription}
              </p>
            </div>

            {calculatorOpen ? (
              activeItem ? (
                <button
                  type="button"
                  onClick={focusCalculator}
                  className="focus-ring group inline-flex h-[49px] shrink-0 items-center justify-center gap-2 rounded-[4px] bg-[#48cae4] px-6 py-3 text-base font-semibold leading-[1.55] tracking-[0.015em] text-[#101828]"
                >
                  {calculator.adjustPrefix} {activeItem.title}
                  <Image
                    src="/engagement-assets/packages-arrow-dark.svg"
                    alt=""
                    width={24}
                    height={24}
                    className="size-6 transition-transform group-hover:translate-x-1"
                  />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => setCalculatorOpen(false)}
                  className="focus-ring inline-flex h-[49px] shrink-0 items-center justify-center gap-2 rounded-[4px] border border-white/60 px-6 py-3 text-base font-semibold leading-[1.55] tracking-[0.015em] text-white hover:bg-white/10"
                >
                  {calculator.cancelLabel}
                  <Image src="/engagement-assets/packages-close.svg" alt="" width={24} height={24} className="size-6" />
                </button>
              )
            ) : (
              <button
                type="button"
                onClick={startCustomBuild}
                className="focus-ring group inline-flex h-[49px] shrink-0 items-center justify-center gap-2 rounded-[4px] bg-[#48cae4] px-6 py-3 text-base font-semibold leading-[1.55] tracking-[0.015em] text-[#101828]"
              >
                {content.customCta.label}
                <Image
                  src="/engagement-assets/packages-arrow-dark.svg"
                  alt=""
                  width={24}
                  height={24}
                  className="size-6 transition-transform group-hover:translate-x-1"
                />
              </button>
            )}
          </div>

          {calculatorOpen ? (
            <div
              ref={calculatorRef}
              className="reveal-up grid scroll-mt-24 items-start gap-8 xl:grid-cols-[minmax(0,592px)_minmax(0,592px)]"
            >
              <div
                data-node-id="1308:14071"
                className="flex min-h-[769px] flex-col gap-6 rounded-[5px] bg-white/10 p-8"
              >
                <RangeControl
                  id="package-people"
                  label={calculator.peopleLabel}
                  value={calculatorState.people}
                  min={1}
                  max={15}
                  minLabel={calculator.peopleMinLabel}
                  maxLabel={calculator.peopleMaxLabel}
                  valueLabel={String(calculatorState.people)}
                  onChange={(value) => updateState("people", value)}
                />
                <RangeControl
                  id="package-duration"
                  label={calculator.durationLabel}
                  value={calculatorState.duration}
                  min={1}
                  max={24}
                  minLabel={calculator.durationMinLabel}
                  maxLabel={calculator.durationMaxLabel}
                  valueLabel={`${calculatorState.duration} ${calculatorState.duration === 1 ? "month" : "months"}`}
                  onChange={(value) => updateState("duration", value)}
                />
                <RangeControl
                  id="package-involvement"
                  label={calculator.involvementLabel}
                  value={calculatorState.involvement}
                  min={0}
                  max={20}
                  minLabel={calculator.involvementMinLabel}
                  maxLabel={calculator.involvementMaxLabel}
                  valueLabel={calculatorState.involvement === 0 ? "None" : `${calculatorState.involvement}h`}
                  onChange={(value) => updateState("involvement", value)}
                />

                <fieldset className="flex w-full flex-col gap-4">
                  <legend className="text-sm font-normal uppercase leading-[1.55] tracking-[0.1em] text-[#dee0e4]">
                    {calculator.capabilitiesLabel}
                  </legend>
                  <div className="flex flex-wrap gap-2">
                    {calculator.capabilities.map((capability) => {
                      const selected = calculatorState.capabilities.includes(capability);
                      return (
                        <button
                          key={capability}
                          type="button"
                          aria-pressed={selected}
                          onClick={() =>
                            updateState(
                              "capabilities",
                              selected
                                ? calculatorState.capabilities.filter((item) => item !== capability)
                                : [...calculatorState.capabilities, capability]
                            )
                          }
                          className={`focus-ring inline-flex h-[38px] items-center gap-2 rounded-[2px] border px-4 py-2 text-sm font-semibold leading-[1.55] tracking-[0.015em] transition-colors ${
                            selected
                              ? "border-[#48cae4] bg-[#48cae4]/10 text-white"
                              : "border-white/20 text-[#dee0e4] hover:bg-white/5"
                          }`}
                        >
                          <Image
                            src={
                              selected
                                ? "/engagement-assets/packages-square-selected.svg"
                                : "/engagement-assets/packages-square.svg"
                            }
                            alt=""
                            width={16}
                            height={16}
                            className="size-4 shrink-0"
                          />
                          {capability}
                        </button>
                      );
                    })}
                  </div>
                </fieldset>

                <ChoiceGroup
                  label={calculator.scopeLabel}
                  options={calculator.scopeOptions}
                  selected={calculatorState.scope}
                  onSelect={(value) => updateState("scope", value)}
                  columns={4}
                />
                <ChoiceGroup
                  label={calculator.managementLabel}
                  options={calculator.managementOptions}
                  selected={calculatorState.management}
                  onSelect={(value) => updateState("management", value)}
                  columns={3}
                />
              </div>

              <aside
                aria-live="polite"
                data-node-id="1308:14156"
                className="flex min-h-[781px] flex-col gap-6 rounded-[5px] bg-[#0077b6] p-8"
              >
                <div className="flex min-h-[173px] flex-col gap-4 border-b border-[#9ca3af] pb-6">
                  <div className="flex flex-col gap-1">
                    <p className="text-base font-normal uppercase leading-[1.6] tracking-[0.1em] text-[#48cae4]">
                      {calculator.recommendationLabel}
                    </p>
                    <h3
                      className={`${headingClassName} text-[34px] font-semibold leading-[1.3] tracking-[-0.03em] text-white sm:text-[40px]`}
                    >
                      {recommendation.model}
                    </h3>
                  </div>
                  <p className="text-base font-normal leading-[1.55] tracking-[0.015em] text-[#dee0e4]">
                    {recommendation.description}
                  </p>
                </div>

                <dl className="flex min-h-[214px] flex-col gap-4 border-b border-[#9ca3af] pb-6">
                  <SummaryRow label="Engagement length" value={recommendation.engagementLength} />
                  <SummaryRow label="Capabilities" value={recommendation.capabilities} />
                  <SummaryRow label="Team managed by" value={recommendation.managedBy} />
                  <SummaryRow label="Outcome owned by" value={recommendation.outcomeOwnedBy} />
                  <SummaryRow label="Your weekly involvement" value={recommendation.weeklyInvolvement} />
                </dl>

                <div className="flex min-h-[25px] items-center justify-between gap-4">
                  <p className="text-sm font-normal uppercase leading-[1.55] tracking-[0.1em] text-[#dee0e4]">
                    {calculator.teamSizeLabel}
                  </p>
                  <p className="text-base font-semibold leading-[1.55] tracking-[0.015em] text-white">
                    {recommendation.teamSize}
                  </p>
                </div>

                <div className="flex min-h-[160px] flex-col gap-4">
                  <p className="text-sm font-normal uppercase leading-[1.55] tracking-[0.1em] text-[#dee0e4]">
                    {calculator.roleMixLabel}
                  </p>
                  {recommendation.roleMix.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {recommendation.roleMix.map((role) => (
                        <span
                          key={role}
                          className="rounded-[2px] border border-white/20 bg-white/20 px-4 py-2 text-sm font-semibold leading-[1.55] tracking-[0.015em] text-white"
                        >
                          {role}
                        </span>
                      ))}
                    </div>
                  ) : null}
                  <p className="text-sm font-normal leading-[1.55] tracking-[0.015em] text-[#9ca3af]">
                    {calculator.roleMixEmpty}
                  </p>
                </div>

                <a
                  href={callHref}
                  className="focus-ring group mt-auto inline-flex h-[49px] w-full items-center justify-center gap-2 rounded-[4px] border border-[#ebedef] bg-white px-6 py-3 text-base font-semibold leading-[1.55] tracking-[0.015em] text-[#101828] hover:bg-[#f3f5f8]"
                >
                  {calculator.callCta.label}
                  <Image
                    src="/engagement-assets/packages-arrow-dark.svg"
                    alt=""
                    width={24}
                    height={24}
                    className="size-6 transition-transform group-hover:translate-x-1"
                  />
                </a>
              </aside>
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}
